import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { DocumentModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import requestSystemList from "@stoneage/App/Handlers/Data/RequestSystemListHandler";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaPdpProductResourcesEvent,
} from "@stoneage/App/Utilities/DataLayer";
import IconFile from "@stoneage/XDL/Icons/IconFile";
import IconVideo from "@stoneage/XDL/Icons/IconVideo";
import { xScreen } from "@stoneage/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    videoDocsType: (getSettingsCollection(state) as any)?.customSettings["videoDocumentType"],
    customerNumber: getBillToCustomerNumber(state),
    impersonation: trackAsImpersonation(state),
    userEmail: state.context.session?.email,
});

const mapDispatchToProps = {
    requestSystemList,
};

type OwnProps = WidgetProps &
    HasProduct &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: InjectableCss;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;

    title?: TypographyProps;
    resourceSectionContainer?: InjectableCss;
    resourceSectionGroup?: InjectableCss;
    sectionTitle?: TypographyProps;
    showMoreLessBtn?: LinkPresentationProps;
    videoIcon?: IconProps;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            padding: 64px 0;
            display: flex;
            flex-wrap: wrap;
            gap: 22px;
            @media print {
                display: block;
            }
        `,
    },
    title: {
        variant: "h3",
        size: "24px",
        lineHeight: "28px",
        color: "common.black",
        css: css`
            width: 100%;
            margin-bottom: 0;
        `,
    },
    resourceSectionContainer: {
        css: css`
            background: ${getColor("common.white")};
            padding: 20px;
            box-shadow: inset 4px 0 0 0 ${getColor("common.ui6")};
            overflow: hidden;
            gap: 16px;
            width: calc(100% / 3 - 15px);
            min-height: 230px;
            @media print {
                margin: 10px 0;
                page-break-inside: avoid;
                width: 100% !important;
                min-height: auto;
            }
            &.support-section {
                background: transparent;
                padding: 0;
                box-shadow: none;
                display: block;
                gap: 0;
                &&& > div {
                    height: 100%;
                }
                &&& .contact-support {
                    gap: 0;
                    width: 100%;
                    height: 100%;
                }
            }
            ${xScreen("lte", "md")} {
                width: calc(100% / 2 - 15px);
            }
            @media all and (max-width: 700px) {
                width: 100%;
                gap: 0;
            }
        `,
    },
    resourceSectionGroup: {
        css: css`
            margin-bottom: 20px;
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    sectionTitle: {
        size: "18px",
        lineHeight: "21px",
        color: "common.black",
        transform: "capitalize",
        weight: "bold",
        css: css`
            margin-bottom: 12px;
            width: 100%;
        `,
    },
    documentGridItem: {
        css: css`
            white-space: nowrap;
            display: flex;
            align-items: center;
            margin-bottom: 8px;
        `,
    },
    documentIcon: {
        src: IconFile,
        size: 12,
        css: css`
            margin-right: 2px;
        `,
    },
    videoIcon: {
        src: IconVideo,
        size: 14,
        css: css`
            margin-right: 3px;
        `,
    },
    documentLink: {
        color: "common.black",
        typographyProps: {
            size: "12px",
            lineHeight: "18px",
            weight: "bold",
            ellipsis: true,
            css: css`
                width: 100%;
            `,
        },
        css: css`
            width: 100%;
        `,
    },
    showMoreLessBtn: {
        typographyProps: {
            size: "12px",
            lineHeight: "18px",
            weight: "bold",
            css: css`
                &&& {
                    color: ${getColor("common.black")};
                }
            `,
        },
    },
};

interface GroupedDocuments {
    [key: string]: DocumentModel[];
}

const styles = documentsStyles;

const ProductDetailsDocuments: React.FC<OwnProps> = ({
    id,
    product: { documents, productNumber },
    requestSystemList,
    videoDocsType,
    customerNumber,
    userEmail,
    impersonation,
}: OwnProps) => {
    const [secondColumnTypes, setSecondColumnTypes] = useState<string[]>([]);

    // XNG-Change
    useEffect(() => {
        requestSystemList({
            slug: "PDPResourcesSecondColumn",
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    const secondColumn: string[] = [];
                    resultProps.apiResult?.result?.values?.forEach(value => {
                        secondColumn.push(value.name.toLowerCase());
                    });
                    setSecondColumnTypes(secondColumn);
                }
            },
        });
    }, []);

    const groupedDocuments: GroupedDocuments[] = documents?.length
        ? documents.reduce((groups: GroupedDocuments[], val) => {
              const type = val.documentType.toLowerCase();

              if (!groups[0]) {
                  groups[0] = {};
              }
              if (secondColumnTypes.includes(type) && !groups[1]) {
                  groups[1] = {};
              }
              const groupIndex = secondColumnTypes.includes(type) ? 1 : 0;
              if (!groups[groupIndex][type]) {
                  groups[groupIndex][type] = [];
              }
              groups[groupIndex][type].push(val);
              return groups;
          }, [])
        : [];

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography as="h3" {...styles.title}>
                {translate("Product Resources")}
            </Typography>
            {groupedDocuments?.length > 0 && (
                <>
                    {groupedDocuments.map((group, index) => {
                        const documentsKeys = Object.keys(group);
                        return (
                            <React.Fragment key={index}>
                                {documentsKeys?.length > 0 && (
                                    <StyledWrapper {...styles.resourceSectionContainer}>
                                        {documentsKeys?.map((docType, idx) => {
                                            return (
                                                <CollapsableSection
                                                    key={idx}
                                                    title={docType}
                                                    documents={group[docType]}
                                                    customerNumber={customerNumber}
                                                    impersonation={impersonation}
                                                    userEmail={userEmail}
                                                    productNumber={productNumber}
                                                    isVideo={docType === videoDocsType}
                                                />
                                            );
                                        })}
                                    </StyledWrapper>
                                )}
                            </React.Fragment>
                        );
                    })}
                </>
            )}

            <StyledWrapper {...styles.resourceSectionContainer} className="support-section">
                <Zone contentId={id} zoneName="SupportWidget" />
            </StyledWrapper>

            {/* <GridContainer {...styles.container}>
                {documents.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            <IconMemo {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer> */}
        </StyledWrapper>
    );
};

const CollapsableSection = ({
    documents,
    title,
    sliceValue,
    isVideo,
    userEmail,
    customerNumber,
    productNumber,
    impersonation,
}: {
    documents: DocumentModel[];
    title: string;
    sliceValue?: number;
    isVideo?: boolean;
    userEmail?: string;
    customerNumber?: string;
    productNumber?: string;
    impersonation: boolean;
}) => {
    return (
        <StyledWrapper {...styles.resourceSectionGroup} className={`${isVideo ? "full-width" : ""}`}>
            <Typography as="h5" {...styles.sectionTitle}>
                {title}
            </Typography>
            {documents
                .sort((doc1, doc2) => doc1.name.localeCompare(doc2.name))
                .map(doc => (
                    <StyledWrapper {...styles.documentGridItem} key={doc.id}>
                        {isVideo && <Icon {...styles.videoIcon} />}
                        {!isVideo && <Icon {...styles.documentIcon} />}
                        <Link
                            target="_new"
                            href={doc.filePath}
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${doc.id}`}
                            // XNG-Change: track GA4
                            onClick={() => {
                                trackGaPdpProductResourcesEvent(
                                    {
                                        clickText: doc.name,
                                        clickUrl: doc.filePath,
                                        productNumber: productNumber || "",
                                    },
                                    impersonation,
                                    userEmail,
                                    customerNumber,
                                );
                            }}
                        >
                            {doc.name}
                        </Link>
                    </StyledWrapper>
                ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsDocuments)),
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
